import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import {
  formatPacificCivilDateLong,
  formatPacificDateTime,
  pacificYmdFromIso,
} from '@/lib/adminPacificTime';
import type {
  AcquisitionFunnelDayRow,
  AcquisitionFunnelStep,
  AdminAcquisitionBackfillResponse,
  AdminAcquisitionFunnelsResponse,
} from '@/lib/types';

const DAYS_PER_PAGE = 7;
const SCROLL_COOLDOWN_MS = 1500;

function formatPercent(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0%';
  if (value >= 0.995) return '100%';
  return `${Math.round(value * 1000) / 10}%`;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function stepColor(index: number, total: number): string {
  const hue = 200 - (index / Math.max(total - 1, 1)) * 120;
  return `hsl(${hue}, 72%, 42%)`;
}

function FunnelStepCell({
  step,
  maxCount,
  index,
  totalSteps,
}: {
  step: AcquisitionFunnelStep;
  maxCount: number;
  index: number;
  totalSteps: number;
}) {
  const widthPct = maxCount > 0 ? Math.max((step.count / maxCount) * 100, 6) : 6;
  const color = stepColor(index, totalSteps);

  return (
    <div className='flex min-w-[132px] max-w-[132px] shrink-0 flex-col gap-1'>
      <p className='truncate text-[10px] font-medium text-gray-600' title={step.label}>
        {step.label}
      </p>
      <div className='h-8 overflow-hidden rounded bg-gray-100'>
        <div
          className='flex h-full items-center justify-center px-1'
          style={{ width: `${widthPct}%`, backgroundColor: color, minWidth: '2.5rem' }}
          title={`${step.label}: ${formatNumber(step.count)} (${formatPercent(step.rate_from_previous)} from previous)`}
        >
          <span className='text-[11px] font-semibold tabular-nums text-white'>
            {formatNumber(step.count)}
          </span>
        </div>
      </div>
      <p className='text-[10px] tabular-nums text-gray-400'>
        {index === 0 ? 'cohort' : formatPercent(step.rate_from_previous)}
      </p>
    </div>
  );
}

function DailyFunnelRow({
  row,
  stepLabels,
}: {
  row: AcquisitionFunnelDayRow;
  stepLabels: string[];
}) {
  const maxCount = Math.max(1, row.cohort_size, ...row.steps.map((step) => step.count));
  const paidStep = row.steps[row.steps.length - 1];
  const landingStep = row.steps[0];
  const isPending = row.pending === true;

  return (
    <div
      className={`rounded-lg border bg-white p-4 shadow-sm ${
        isPending ? 'border-dashed border-gray-300 opacity-80' : 'border-gray-200'
      }`}
    >
      <div className='mb-3 flex flex-wrap items-end justify-between gap-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>
            {formatPacificCivilDateLong(row.date)}
            {isPending ? (
              <span className='ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500'>
                Loading…
              </span>
            ) : null}
          </p>
          <p className='text-xs text-gray-500'>
            Cohort {formatNumber(row.cohort_size)} landings ·{' '}
            {formatPercent(paidStep?.rate_from_landing ?? 0)} paid
          </p>
        </div>
        <p className='text-xs tabular-nums text-gray-500'>
          {formatNumber(landingStep?.count ?? 0)} → {formatNumber(paidStep?.count ?? 0)}
        </p>
      </div>
      <div className='overflow-x-auto pb-1'>
        <div className='flex min-w-max gap-2'>
          {row.steps.map((step, index) => (
            <FunnelStepCell
              key={`${row.date}-${stepLabels[index] ?? step.event_key}`}
              step={step}
              maxCount={maxCount}
              index={index}
              totalSteps={row.steps.length}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function BackfillProgress({
  complete,
  total,
  pending,
  rateLimitedUntil,
}: {
  complete: number;
  total: number;
  pending: number;
  rateLimitedUntil: string | null;
}) {
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;

  return (
    <div className='rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700'>
      <div className='mb-1 flex flex-wrap items-center justify-between gap-2'>
        <span>
          Cached <span className='font-semibold tabular-nums'>{complete}</span> of{' '}
          <span className='tabular-nums'>{total}</span> days ({pct}%)
        </span>
        {pending > 0 ? (
          <span className='text-gray-500'>
            <span className='tabular-nums'>{pending}</span> queued for backfill
          </span>
        ) : null}
      </div>
      <div className='h-1.5 overflow-hidden rounded-full bg-gray-100'>
        <div className='h-full rounded-full bg-sky-500 transition-all' style={{ width: `${pct}%` }} />
      </div>
      {rateLimitedUntil ? (
        <p className='mt-1 text-[11px] text-amber-700'>
          Amplitude rate limited until {formatPacificDateTime(rateLimitedUntil)} (
          {pacificYmdFromIso(rateLimitedUntil)} Pacific)
        </p>
      ) : null}
    </div>
  );
}

function AdminAcquisitionPage() {
  const { getToken } = useAuth();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const lastScrollFetchAtRef = useRef(0);

  const funnelsQuery = useInfiniteQuery({
    queryKey: ['admin', 'acquisition', 'funnels'],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        days: String(DAYS_PER_PAGE),
      });
      if (pageParam) {
        params.set('end_date', pageParam);
      }
      return apiJson<AdminAcquisitionFunnelsResponse>(
        getToken,
        `/v1/admin/acquisition/funnels?${params.toString()}`,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more && lastPage.next_end_date ? lastPage.next_end_date : null,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      const hasPendingRows = pages.some((page) => page.rows.some((row) => row.pending));
      const backfillPending = pages[0]?.backfill.pending ?? 0;
      return hasPendingRows || backfillPending > 0 ? 15000 : false;
    },
  });

  const backfillStatusQuery = useQuery({
    queryKey: ['admin', 'acquisition', 'backfill'],
    queryFn: () =>
      apiJson<AdminAcquisitionBackfillResponse>(getToken, '/v1/admin/acquisition/backfill'),
    refetchInterval: (query) => {
      const pending = query.state.data?.backfill.pending ?? 0;
      return pending > 0 ? 15000 : 60000;
    },
  });

  const backfillMutation = useMutation({
    mutationFn: () =>
      apiJson<AdminAcquisitionBackfillResponse>(
        getToken,
        '/v1/admin/acquisition/backfill?full_history=true',
        { method: 'POST' },
      ),
    onSuccess: () => {
      void funnelsQuery.refetch();
      void backfillStatusQuery.refetch();
    },
  });

  const pages = funnelsQuery.data?.pages ?? [];
  const rows = useMemo(
    () => pages.flatMap((page) => page.rows),
    [pages],
  );
  const stepLabels = pages[0]?.step_labels ?? [];
  const amplitudeError = pages.find((page) => page.amplitude_error)?.amplitude_error ?? null;
  const amplitudeStaleAt =
    pages.find((page) => page.amplitude_stale_at)?.amplitude_stale_at ?? null;
  const backfill =
    backfillStatusQuery.data?.backfill ??
    pages[pages.length - 1]?.backfill ?? {
      pending: 0,
      complete: 0,
      total: 0,
      rate_limited_until: null,
    };

  const handleLoadMore = useCallback(() => {
    const now = Date.now();
    if (now - lastScrollFetchAtRef.current < SCROLL_COOLDOWN_MS) {
      return;
    }
    if (
      funnelsQuery.hasNextPage &&
      !funnelsQuery.isFetchingNextPage &&
      !funnelsQuery.isFetching
    ) {
      lastScrollFetchAtRef.current = now;
      void funnelsQuery.fetchNextPage();
    }
  }, [funnelsQuery]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: '240px 0px', threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [handleLoadMore, rows.length]);

  const isInitialLoading = funnelsQuery.isLoading && rows.length === 0;

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Acquisition</h1>
          <p className='text-sm text-gray-500'>
            Mobile onboarding funnels by Pacific day — Landing Screen through Subscription Paid.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <button
            type='button'
            onClick={() => backfillMutation.mutate()}
            disabled={backfillMutation.isPending}
            className='inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60'
          >
            Backfill history
          </button>
          <button
            type='button'
            onClick={() => funnelsQuery.refetch()}
            disabled={funnelsQuery.isFetching}
            className='inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60'
          >
            <FiRefreshCw className={funnelsQuery.isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className='rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900'>
        Each row is a cohort that opened the app on that day (Landing Screen). Steps count users
        who reached each screen after landing within a 30-day window. Historical days are cached in
        Redis and backfilled slowly to stay under Amplitude rate limits.
      </div>

      <BackfillProgress
        complete={backfill.complete}
        total={backfill.total}
        pending={backfill.pending}
        rateLimitedUntil={backfill.rate_limited_until}
      />

      {amplitudeError ? (
        <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
          {amplitudeStaleAt ? (
            <>
              <span className='font-semibold'>Showing cached Amplitude data.</span> {amplitudeError}{' '}
              Cached at {formatPacificDateTime(amplitudeStaleAt)} (
              {pacificYmdFromIso(amplitudeStaleAt)} Pacific).
            </>
          ) : (
            <>
              <span className='font-semibold'>Amplitude export unavailable.</span> {amplitudeError}
            </>
          )}
        </div>
      ) : null}

      {isInitialLoading ? (
        <div className='flex items-center justify-center py-16'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : null}

      {!isInitialLoading && rows.length === 0 ? (
        <p className='text-sm text-gray-500'>No funnel data available yet.</p>
      ) : null}

      <div className='space-y-4'>
        {rows.map((row) => (
          <DailyFunnelRow key={row.date} row={row} stepLabels={stepLabels} />
        ))}
      </div>

      <div ref={loadMoreRef} className='flex min-h-12 items-center justify-center py-4'>
        {funnelsQuery.isFetchingNextPage ? (
          <div className='h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        ) : funnelsQuery.hasNextPage ? (
          <p className='text-xs text-gray-400'>Scroll for earlier days…</p>
        ) : rows.length > 0 ? (
          <p className='text-xs text-gray-400'>End of available history.</p>
        ) : null}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/acquisition')({
  component: AdminAcquisitionPage,
});
